import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  Download,
  Trash2,
  Shield,
  Eye,
  FileText,
  User,
  Mail,
  Calendar,
  CreditCard,
  History,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DataManagementPage({ user, onLogout, refreshUserData }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    fetchUserData();
  }, [user, navigate]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user/data`);
      setUserData(response.data);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Erreur lors du chargement de vos données");
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/user/data/export`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mes-donnees-middleman-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Vos données ont été téléchargées");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Erreur lors de l'exportation de vos données");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== user.email) {
      toast.error("Veuillez entrer votre adresse courriel correctement");
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/user/data`);
      toast.success("Votre compte a été supprimé");
      onLogout();
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Erreur lors de la suppression de votre compte");
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-brand-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-brand-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800">Gestion de mes données</h1>
          <p className="text-slate-600 mt-2">
            Conformément à la Loi 25 du Québec, vous avez le droit d'accéder, de modifier et de supprimer vos données personnelles.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* User Data Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-brand-600" />
                  Mes informations personnelles
                </CardTitle>
                <CardDescription>
                  Aperçu des données que nous détenons sur vous
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Nom</p>
                      <p className="font-medium">{userData?.name || user.name || "Non renseigné"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Courriel</p>
                      <p className="font-medium">{userData?.email || user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Date d'inscription</p>
                      <p className="font-medium">
                        {userData?.created_at 
                          ? new Date(userData.created_at).toLocaleDateString('fr-CA')
                          : "Non disponible"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <History className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Nombre de transactions</p>
                      <p className="font-medium">{userData?.transaction_count || 0}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Export */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-brand-600" />
                  Exporter mes données
                </CardTitle>
                <CardDescription>
                  Téléchargez une copie de toutes vos données personnelles (droit à la portabilité)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Le fichier exporté contiendra :
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm text-slate-600 mb-6">
                  <li>Vos informations de profil</li>
                  <li>L'historique de vos transactions</li>
                  <li>Vos préférences et paramètres</li>
                </ul>
                <Button onClick={handleExportData} className="gap-2">
                  <Download className="h-4 w-4" />
                  Télécharger mes données (JSON)
                </Button>
              </CardContent>
            </Card>

            {/* Delete Account */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="h-5 w-5" />
                  Supprimer mon compte
                </CardTitle>
                <CardDescription>
                  Cette action est irréversible. Toutes vos données seront supprimées.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div className="text-sm text-red-800">
                      <p className="font-medium mb-1">Attention !</p>
                      <p>La suppression de votre compte entraînera :</p>
                      <ul className="list-disc pl-4 mt-2 space-y-1">
                        <li>La suppression définitive de vos informations personnelles</li>
                        <li>La perte de l'accès à l'historique de vos transactions</li>
                        <li>L'impossibilité de récupérer votre compte</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowDeleteDialog(true)}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer définitivement mon compte
                </Button>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-brand-600" />
                  Autres demandes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Pour toute autre demande concernant vos données personnelles (rectification, questions, plaintes), 
                  contactez notre responsable de la protection des renseignements personnels :
                </p>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p><strong>Courriel :</strong> privacy@middleman.ca</p>
                  <p className="text-sm text-slate-500 mt-1">Délai de réponse : 30 jours maximum</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Pour confirmer, veuillez entrer votre adresse courriel.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="confirm-email">Entrez votre courriel pour confirmer : <strong>{user.email}</strong></Label>
            <Input
              id="confirm-email"
              type="email"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={user.email}
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmation !== user.email}
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Suppression...</>
              ) : (
                "Supprimer définitivement"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
